import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import { notify } from '~/app/helpers/notify';
import { validateOperatingTimeOrCount } from '~/app/helpers/validation';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import { useStoreActions, useStoreState } from '~/store';
import {
    canEditWorkOrderOperatingCount,
    canEditWorkOrderOperatingTime,
    formatOperatingMetricInputValue,
    isOperatingMetricInRange,
    isOperatingTimeRequired,
    isOperatingCountRequired,
    operatingMetricsEqual,
    parseOperatingMetricInputValue,
    OPERATING_METRIC_MAX,
    OPERATING_METRIC_MIN,
} from './helper';
import { WorkOrderAutoSaveFieldKey, WorkOrderDetail } from '~/workorder/types';
import classes from './style.module.scss';
import Text from '~/app/modules/form/Text';

export interface OperatingTimeProps {
    workOrderId: WorkOrderDetail['id'];
    status: WorkOrderDetail['status'];
    excavator: WorkOrderDetail['excavator'];
    operatingTime: WorkOrderDetail['operatingTime'];
    operatingCount: WorkOrderDetail['operatingCount'];
}

export type OperatingTimeComponent = React.FC<OperatingTimeProps>;

type OperatingMetricValidation = { type: 'underLimit' | 'overLimit'; difference: number };

type OperatingMetricFieldKey = Extract<WorkOrderAutoSaveFieldKey, 'operatingTime' | 'operatingCount'>;

type PendingOperatingSave = {
    key: OperatingMetricFieldKey;
    parsedTime: number | null;
    parsedCount: number | null;
    warning: OperatingMetricValidation;
};

const EMPTY_VALUE = '—';

const formatMetric = (value: number | null): string => {
    if (value === null || value === undefined) {
        return EMPTY_VALUE;
    }
    return String(value);
};

const OperatingTime: OperatingTimeComponent = ({ workOrderId, status, excavator, operatingTime, operatingCount }) => {
    const { t } = useTranslation('workorder');
    const { t: tAPIResponseMessages } = useTranslation('apiResponseMessages');
    const { t: tOperatingTimeError } = useTranslation('operatingTime.error');
    const { t: tOperatingCountError } = useTranslation('operatingCount.error');
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const { setLiveOperatingHours, allowManualOperatingTimeUpdate } = useStoreState(state => state.workOrder.workOrderGeneralSettings);
    const autoSaveWorkOrderData = useStoreActions(actions => actions.workOrder.autoSaveWorkOrderData);

    const showTime = !!excavator?.isOperatingTimeVisible;
    const showCount = !!excavator?.isOperatingCountVisible;

    const visible = useMemo(() => showTime || showCount, [showTime, showCount]);

    const isTimeRequired = useMemo(
        () => isOperatingTimeRequired(excavator),
        [excavator],
    );

    const isCountRequired = useMemo(
        () => isOperatingCountRequired(excavator),
        [excavator],
    );

    const canEditTime = useMemo(
        () => canEditWorkOrderOperatingTime({
            status,
            setLiveOperatingHours,
            allowManualOperatingTimeUpdate,
            tracker: excavator?.tracker,
        }),
        [status, setLiveOperatingHours, allowManualOperatingTimeUpdate, excavator?.tracker],
    );

    const canEditCount = useMemo(
        () => canEditWorkOrderOperatingCount(status),
        [status],
    );

    const [timeInput, setTimeInput] = useState(() => formatOperatingMetricInputValue(operatingTime));
    const [countInput, setCountInput] = useState(() => formatOperatingMetricInputValue(operatingCount));
    const [pendingKey, setPendingKey] = useState<OperatingMetricFieldKey | null>(null);
    const [warningDialog, setWarningDialog] = useState<PendingOperatingSave | null>(null);

    const savedTimeRef = useRef(operatingTime);
    const savedCountRef = useRef(operatingCount);

    useEffect(() => {
        savedTimeRef.current = operatingTime;
        setTimeInput(formatOperatingMetricInputValue(operatingTime));
    }, [operatingTime]);

    useEffect(() => {
        savedCountRef.current = operatingCount;
        setCountInput(formatOperatingMetricInputValue(operatingCount));
    }, [operatingCount]);

    const persistOperatingField = useCallback((
        key: OperatingMetricFieldKey,
        nextValue: number | null,
    ) => {
        if (netWorkStatus !== 0 || pendingKey) {
            return;
        }

        const savedValue = key === 'operatingTime' ? savedTimeRef.current : savedCountRef.current;
        if (operatingMetricsEqual(nextValue, savedValue)) {
            return;
        }

        setPendingKey(key);

        autoSaveWorkOrderData({
            key,
            workOrderId,
            value: formatOperatingMetricInputValue(nextValue),
        })
            .then(response => response.success && notify(tAPIResponseMessages('changesSaved'), 'success'))
            .catch(() => {
                notify(t('detail.error.operatingDataUpdateFailed'), 'error');
                if (key === 'operatingTime') {
                    setTimeInput(formatOperatingMetricInputValue(savedTimeRef.current));
                } else {
                    setCountInput(formatOperatingMetricInputValue(savedCountRef.current));
                }
            })
            .finally(() => setPendingKey(null));

    }, [autoSaveWorkOrderData, netWorkStatus, pendingKey, tAPIResponseMessages, t, workOrderId]);

    const openWarningDialog = useCallback((
        key: OperatingMetricFieldKey,
        parsedTime: number | null,
        parsedCount: number | null,
        warning: OperatingMetricValidation,
    ) => {
        setWarningDialog({ key, parsedTime, parsedCount, warning });
    }, []);

    const handleWarningCancel = useCallback(() => {
        if (!warningDialog) {
            return;
        }
        if (warningDialog.key === 'operatingTime') {
            setTimeInput(formatOperatingMetricInputValue(savedTimeRef.current));
        } else {
            setCountInput(formatOperatingMetricInputValue(savedCountRef.current));
        }
        setWarningDialog(null);
    }, [warningDialog]);

    const handleWarningContinue = useCallback(() => {
        if (!warningDialog) {
            return;
        }
        const { key, parsedTime, parsedCount } = warningDialog;
        setWarningDialog(null);
        persistOperatingField(key, key === 'operatingTime' ? parsedTime : parsedCount);
    }, [persistOperatingField, warningDialog]);

    const notifyOutOfRange = useCallback(() => {
        notify(
            t('detail.error.operatingValueOutOfRange', {
                min: OPERATING_METRIC_MIN,
                max: OPERATING_METRIC_MAX,
            }),
            'error',
        );
    }, [t]);

    const handleTimeBlur = useCallback(() => {
        if (!canEditTime || warningDialog) {
            return;
        }
        const parsedTime = parseOperatingMetricInputValue(timeInput);
        const parsedCount = parseOperatingMetricInputValue(countInput);
        if (!isOperatingMetricInRange(parsedTime)) {
            notifyOutOfRange();
            setTimeInput(formatOperatingMetricInputValue(savedTimeRef.current));
            return;
        }
        const oldTime = savedTimeRef.current ?? 0;
        const newTime = parsedTime ?? 0;
        const warning = validateOperatingTimeOrCount(oldTime, newTime);
        if (warning) {
            openWarningDialog('operatingTime', parsedTime, parsedCount, warning as OperatingMetricValidation);
            return;
        }
        persistOperatingField('operatingTime', parsedTime);
    }, [canEditTime, countInput, notifyOutOfRange, openWarningDialog, persistOperatingField, timeInput, warningDialog]);

    const handleCountBlur = useCallback(() => {
        if (!canEditCount || warningDialog) {
            return;
        }
        const parsedTime = parseOperatingMetricInputValue(timeInput);
        const parsedCount = parseOperatingMetricInputValue(countInput);
        if (!isOperatingMetricInRange(parsedCount)) {
            notifyOutOfRange();
            setCountInput(formatOperatingMetricInputValue(savedCountRef.current));
            return;
        }
        const oldCount = savedCountRef.current ?? 0;
        const newCount = parsedCount ?? 0;
        const warning = validateOperatingTimeOrCount(oldCount, newCount);
        if (warning) {
            openWarningDialog('operatingCount', parsedTime, parsedCount, warning as OperatingMetricValidation);
            return;
        }
        persistOperatingField('operatingCount', parsedCount);
    }, [canEditCount, countInput, notifyOutOfRange, openWarningDialog, persistOperatingField, timeInput, warningDialog]);

    const handleTimeInputChange = useCallback((raw: string) => {
        const normalized = raw.replace(',', '.');
        if (normalized === '' || /^\d*$/.test(normalized)) {
            const parsed = parseOperatingMetricInputValue(normalized);
            if (parsed !== null && parsed > OPERATING_METRIC_MAX) {
                return;
            }
            setTimeInput(normalized);
        }
    }, []);

    const handleCountInputChange = useCallback((raw: string) => {
        const normalized = raw.replace(',', '.');
        if (normalized === '' || /^\d*$/.test(normalized)) {
            const parsed = parseOperatingMetricInputValue(normalized);
            if (parsed !== null && parsed > OPERATING_METRIC_MAX) {
                return;
            }
            setCountInput(normalized);
        }
    }, []);

    const warningMessage = useMemo(() => {
        if (!warningDialog) {
            return '';
        }
        const { warning, key } = warningDialog;
        const translate = key === 'operatingTime' ? tOperatingTimeError : tOperatingCountError;
        return translate(warning.type, { difference: warning.difference });
    }, [warningDialog, tOperatingCountError, tOperatingTimeError]);

    if (!visible) {
        return null;
    }

    const inputsDisabled = !!pendingKey || !!warningDialog;

    return (
        <>
            <div className={classes['card']}>
                <h2 className={classes['title']}>{t('detail.operatingData')}</h2>
                <div className={classes['grid']}>
                    {showTime && (
                        <div>
                            <span className={classes['field-label']}>
                                {t('detail.operatingTimeHours')}
                                {isTimeRequired && <span className={classes['required-mark']}>*</span>}
                            </span>
                            {canEditTime ? (
                                <Text
                                    type="number"
                                    min={OPERATING_METRIC_MIN}
                                    max={OPERATING_METRIC_MAX}
                                    step="1"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    className={classes['field-input']}
                                    value={timeInput}
                                    disabled={inputsDisabled}
                                    required={isTimeRequired}
                                    onChange={e => handleTimeInputChange(e.target.value)}
                                    onBlur={handleTimeBlur}
                                />
                            ) : (
                                <span className={classes['field-value']}>{formatMetric(operatingTime)}</span>
                            )}
                        </div>
                    )}
                    {showCount && (
                        <div>
                            <span className={classes['field-label']}>
                                {t('detail.operatingCountHours')}
                                {isCountRequired && <span className={classes['required-mark']}>*</span>}
                            </span>
                            {canEditCount ? (
                                <Text
                                    type="number"
                                    min={OPERATING_METRIC_MIN}
                                    max={OPERATING_METRIC_MAX}
                                    step="1"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    className={classes['field-input']}
                                    value={countInput}
                                    disabled={inputsDisabled}
                                    required={isCountRequired}
                                    onChange={e => handleCountInputChange(e.target.value)}
                                    onBlur={handleCountBlur}
                                />
                            ) : (
                                <span className={classes['field-value']}>{formatMetric(operatingCount)}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmationMenu
                isOpen={!!warningDialog}
                confirmationText={warningMessage}
                onConfirm={handleWarningContinue}
                onCancel={handleWarningCancel}
            />
        </>
    );
};

export default OperatingTime;
